import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronLeft, ChevronRight, Edit3, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "../../components/Button/Button";
import { Card } from "../../components/Card/Card";
import { DataTable, type Column } from "../../components/DataTable/DataTable";
import { FilterBar } from "../../components/FilterBar/FilterBar";
import { StatusPill } from "../../components/StatusPill/StatusPill";
import { TopNav } from "../../components/TopNav/TopNav";
import { type CaseRow, api } from "../../lib/api";
import { formatDate, formatEuro } from "../../lib/format";
import { useSelection } from "../../lib/SelectionContext";

import { CaseEditDialog } from "../Casos/CaseEditDialog";
import styles from "./ConsultarCasosPage.module.css";

interface Filters {
  search: string;
  date: string;
  estado: string;
  tipos: string[];
}

const EMPTY_FILTERS: Filters = { search: "", date: "", estado: "", tipos: [] };
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 100;
const MAX_EXPORT = 290;

export default function ConsultarCasosPage() {
  const navigate = useNavigate();
  const { selected, toggle, addMany, removeMany, clear } = useSelection();

  const [cases, setCases] = useState<CaseRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeRef = useRef<HTMLDivElement>(null);
  const [tipoOptions, setTipoOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);

  const [editing, setEditing] = useState<CaseRow | null>(null);
  const [creating, setCreating] = useState(false);

  const pageRows = cases;
  const allOnPageSelected = pageRows.length > 0 && pageRows.every((r) => selected.has(r.id));
  const atLimit = selected.size >= MAX_EXPORT;

  function togglePage() {
    if (allOnPageSelected) {
      removeMany(pageRows.map((r) => r.id));
      return;
    }
    const remaining = MAX_EXPORT - selected.size;
    const toAdd = pageRows.filter((r) => !selected.has(r.id)).slice(0, remaining);
    addMany(toAdd.map((r) => r.id));
    if (toAdd.length < pageRows.filter((r) => !selected.has(r.id)).length) {
      setError(`Limite de ${MAX_EXPORT} casos por exportação atingido.`);
    }
  }

  async function fetchCases(p: number, f: Filters, size = pageSize) {
    const params = new URLSearchParams();
    params.set("page", String(p));
    params.set("page_size", String(size));
    params.set("ordering", "-created_at");
    if (f.search.trim()) params.set("search", f.search.trim());
    if (f.estado) params.set("Estado", f.estado);
    if (f.tipos.length > 0) params.set("TipoDocto__in", f.tipos.join(","));
    if (f.date) params.set("created_at__date", f.date);
    const c = await api.listCases(params);
    setCases(c.results);
    setTotalCount(c.count);
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      await fetchCases(page, applied);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os casos.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    api
      .caseTipoOptions(true)
      .then(setTipoOptions)
      .catch(() => setTipoOptions([]));
  }, []);

  async function applyFilters() {
    setPage(1);
    setApplied(filters);
    setLoading(true);
    try {
      await fetchCases(1, filters);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os casos.");
    } finally {
      setLoading(false);
    }
  }

  async function clearFilters() {
    setPage(1);
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setLoading(true);
    try {
      await fetchCases(1, EMPTY_FILTERS);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os casos.");
    } finally {
      setLoading(false);
    }
  }

  async function goToPage(p: number) {
    setPage(p);
    setLoading(true);
    try {
      await fetchCases(p, applied);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os casos.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!pageSizeOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (pageSizeRef.current && !pageSizeRef.current.contains(event.target as Node)) {
        setPageSizeOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setPageSizeOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [pageSizeOpen]);

  async function changePageSize(size: number) {
    setPageSizeOpen(false);
    if (size === pageSize) return;
    setPageSize(size);
    setPage(1);
    setLoading(true);
    try {
      await fetchCases(1, applied, size);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os casos.");
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const columns: Column<CaseRow>[] = [
    {
      key: "select",
      header: (
        <label className={styles.checkbox} aria-label="Selecionar página">
          <input type="checkbox" checked={allOnPageSelected} onChange={togglePage} />
        </label>
      ),
      render: (row) => {
        const isEnviado = row.Estado === "Enviado";
        return (
          <label
            className={styles.checkbox}
            aria-label={`Selecionar caso ${row.MovNum}`}
            title={isEnviado ? "Casos já enviados não podem ser selecionados" : undefined}
          >
            <input
              type="checkbox"
              checked={selected.has(row.id)}
              disabled={isEnviado}
              onChange={() => {
                if (!selected.has(row.id) && atLimit) {
                  setError(`Limite de ${MAX_EXPORT} casos por exportação atingido.`);
                  return;
                }
                toggle(row.id);
              }}
            />
          </label>
        );
      },
      width: "44px",
      hideable: false,
    },
    {
      key: "cod",
      header: "CodProjeto",
      label: "CodProjeto",
      render: (row) => row.CodProjeto,
      sortable: true,
      sortValue: (row) => row.CodProjeto,
    },
    {
      key: "mov",
      header: "MovNum",
      label: "MovNum",
      render: (row) => `${String(row.MovNum).padStart(7, "0")}`,
      sortable: true,
      sortValue: (row) => row.MovNum,
    },
    {
      key: "nif",
      header: "NIFDestinatário",
      label: "NIF Destinatário",
      render: (row) => row.NIFDestinatario,
      sortable: true,
      sortValue: (row) => row.NIFDestinatario,
    },
    {
      key: "vat",
      header: "VATFornecedor",
      label: "VAT Fornecedor",
      render: (row) => row.VATFornecedor || "",
      sortable: true,
      sortValue: (row) => row.VATFornecedor,
    },
    {
      key: "fornecedor",
      header: "Nome Fornecedor",
      label: "Nome do fornecedor",
      render: (row) => row.NomeFornecedor || "",
      sortable: true,
      sortValue: (row) => row.NomeFornecedor,
    },
    {
      key: "valor",
      header: "ValorImputado",
      label: "Valor imputado",
      render: (row) => formatEuro(row.ValorImputado),
      sortable: true,
      sortValue: (row) => (row.ValorImputado ? Number(row.ValorImputado) : null),
    },
    {
      key: "data",
      header: "DataDocto",
      label: "Data do documento",
      render: (row) => formatDate(row.DataDocto),
      sortable: true,
      sortValue: (row) => (row.DataDocto ? new Date(row.DataDocto) : null),
    },
    {
      key: "tipo",
      header: "TipoDocto",
      label: "Tipo de documento",
      render: (row) => row.TipoDocto,
      sortable: true,
      sortValue: (row) => row.TipoDocto,
    },
    {
      key: "estado",
      header: "Estado",
      label: "Estado",
      render: (row) => {
        switch (row.Estado) {
          case "Enviado":
            return <StatusPill tone="sent">Enviado</StatusPill>;
          case "Por Reenviar":
            return <StatusPill tone="resend">Por Reenviar</StatusPill>;
          default:
            return <StatusPill tone="to-send">Por Enviar</StatusPill>;
        }
      },
      width: "130px",
      sortable: true,
      sortValue: (row) => row.Estado,
    },
    {
      key: "acoes",
      header: "Ações",
      label: "Ações",
      render: (row) => (
        <div className={styles.rowActions}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => setEditing(row)}
            aria-label="Editar caso"
          >
            <Edit3 size={14} />
          </button>
        </div>
      ),
      width: "70px",
      hideable: false,
    },
  ];

  return (
    <div className={styles.page}>
      <TopNav />

      <main className={styles.main}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        <Card
          toolbar={
            <div className={styles.toolbarGroup}>
              <button type="button" className={styles.toolbarToggle} onClick={togglePage}>
                <span className={styles.checkbox}>
                  <input type="checkbox" checked={allOnPageSelected} readOnly />
                </span>
                Selecionar página
              </button>
              {selected.size > 0 && (
                <>
                  <span className={styles.selectionCount}>{selected.size} selecionado(s)</span>
                  <button type="button" className={styles.toolbarToggle} onClick={clear}>
                    Limpar seleção
                  </button>
                  <button
                    type="button"
                    className={styles.toolbarSend}
                    onClick={() => navigate("/enviar")}
                  >
                    <Send size={12} />
                    Ir para Enviar
                  </button>
                </>
              )}
            </div>
          }
        >
          <FilterBar
            searchValue={filters.search}
            onSearchChange={(v) => setFilters((f) => ({ ...f, search: v }))}
            dateValue={filters.date}
            onDateChange={(v) => setFilters((f) => ({ ...f, date: v }))}
            estadoValue={filters.estado}
            onEstadoChange={(v) => setFilters((f) => ({ ...f, estado: v }))}
            estadoOptions={[
              { value: "Por Enviar", label: "Por Enviar" },
              { value: "Enviado", label: "Enviado" },
              { value: "Por Reenviar", label: "Por Reenviar" },
            ]}
            tipoValues={filters.tipos}
            onTipoChange={(v) => setFilters((f) => ({ ...f, tipos: v }))}
            tipoOptions={tipoOptions}
            tipoPlaceholder="Tipo de documento"
            onApply={() => void applyFilters()}
            onClear={() => void clearFilters()}
            trailing={
              <Button variant="soft" onClick={() => setCreating(true)}>
                Criar caso
              </Button>
            }
          />
          <DataTable
            columns={columns}
            rows={cases}
            rowKey={(r) => r.id}
            empty={loading ? "A carregar…" : "Sem casos registados."}
            maxHeight="540px"
            storageKey="consultarCasos"
          />
          {totalCount > 0 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                onClick={() => void goToPage(page - 1)}
                disabled={page <= 1 || loading}
                aria-label="Página anterior"
              >
                <ChevronLeft size={16} />
              </button>
              <span className={styles.pageInfo}>
                Página {page} de {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                onClick={() => void goToPage(page + 1)}
                disabled={page >= totalPages || loading}
                aria-label="Próxima página"
              >
                <ChevronRight size={16} />
              </button>
              <div className={styles.pageSize} ref={pageSizeRef}>
                <span>Linhas por página</span>
                <button
                  type="button"
                  className={`${styles.pageSizeChip} ${pageSizeOpen ? styles.pageSizeChipOpen : ""}`}
                  onClick={() => setPageSizeOpen((v) => !v)}
                  disabled={loading}
                  aria-haspopup="listbox"
                  aria-expanded={pageSizeOpen}
                  aria-label={`Linhas por página: ${pageSize}`}
                >
                  <span className={styles.pageSizeValue}>{pageSize}</span>
                  <ChevronDown
                    size={14}
                    aria-hidden
                    className={`${styles.pageSizeChevron} ${
                      pageSizeOpen ? styles.pageSizeChevronOpen : ""
                    }`}
                  />
                </button>
                {pageSizeOpen && (
                  <div className={styles.pageSizeMenu} role="listbox">
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <button
                        key={n}
                        type="button"
                        role="option"
                        aria-selected={n === pageSize}
                        className={`${styles.pageSizeItem} ${
                          n === pageSize ? styles.pageSizeItemActive : ""
                        }`}
                        onClick={() => void changePageSize(n)}
                      >
                        {n}
                        {n === pageSize && <Check size={14} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </main>

      <CaseEditDialog
        open={editing !== null}
        caseRow={editing}
        mode="edit"
        onClose={() => setEditing(null)}
        onSaved={() => {
          void refresh();
        }}
      />

      <CaseEditDialog
        open={creating}
        caseRow={null}
        mode="create"
        onClose={() => setCreating(false)}
        onSaved={() => {
          void refresh();
        }}
      />
    </div>
  );
}
